import React, {useCallback, useContext, useEffect, useState, useRef} from 'react';
import Box from "@mui/material/Box";
import { ThemeProvider } from '@mui/material/styles';
import { brandAuthTheme } from '../../../constants/brandAuthTheme';
import {Controller, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup/dist/yup";
import {postNewItemValidationSchema} from "../../../constants/validationSchemas";
import Grid from "@mui/material/GridLegacy";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import {Autocomplete, CircularProgress, debounce, FormControl, InputLabel, MenuItem, Select, Switch, Checkbox, ListItemText, Chip} from "@mui/material";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {DatePicker} from "@mui/x-date-pickers";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import FormLabel from "@mui/material/FormLabel";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import MedicationIcon from '@mui/icons-material/Medication';
import {NotificationType} from "../../../constants/global";
import {routeConfigs} from "../../../navigation/routeConfig";
import {useNavigate, useParams} from "react-router-dom";
import {ToasterContext} from "../../../components/shared/Toaster/ToasterContext";
import {useQueryPackages} from "../../../queries/product/hooks/useQueryPackages";
import {useQueryUniqueProduct} from "../../../queries/product/hooks/useQueryUniqueProduct";
import {useMutationUpdateProduct} from "../../../queries/product/hooks/useMutationUpdateProduct";
import dayjs from "dayjs";
import {UserContext} from "../../../hooks/userContext";
import {useQueryUniqueUsers} from "../../../queries/pharmacy/hooks/useQueryUniqueUsers";
import {useAuth} from "../../../hooks/useAuth";
import axios from "axios";
import {normalizeNdc} from "../../../components/utils/utils";
import BrandLoader from "../../../components/shared/BrandLoader/BrandLoader";

const INK = '#1E293B';
const INK_SOFT = '#475569';
const LINE = '#E2E8F0';
const LINE_SOFT = '#F1F5F9';
const BODY_FONT = '"Source Sans 3", sans-serif';

const SectionHeader = ({ title, subtitle }: { title: string; subtitle?: string }) => (
  <Box sx={{ px: 3, pt: 2.5, pb: subtitle ? 0 : 0.5 }}>
    <Box
      component="h2"
      sx={{
        m: 0,
        fontFamily: '"Lexend", sans-serif',
        fontWeight: 700,
        fontSize: 18,
        letterSpacing: '-0.01em',
        color: INK,
        lineHeight: 1.3,
      }}
    >
      {title}
    </Box>
    {subtitle && (
      <Box sx={{ fontSize: 13, color: INK_SOFT, fontFamily: BODY_FONT, mt: 0.25 }}>{subtitle}</Box>
    )}
  </Box>
);

const DrugImagePanel = ({ imageUrl, placeholder }: { imageUrl?: string | null; placeholder: string }) => (
  <Box
    sx={{
      border: `1px solid ${LINE_SOFT}`,
      borderRadius: '10px',
      bgcolor: '#F1F5F9',
      p: 2,
      height: '100%',
      minHeight: 280,
      display: 'flex',
      flexDirection: 'column',
      gap: 1.5,
    }}
  >
    <Box
      sx={{
        fontSize: '11px',
        fontWeight: 600,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: INK_SOFT,
        fontFamily: BODY_FONT,
      }}
    >
      Drug image
    </Box>
    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {imageUrl ? (
        <Box
          component="img"
          src={imageUrl}
          alt="Drug"
          sx={{
            maxWidth: '100%',
            maxHeight: 280,
            borderRadius: '8px',
            border: `1px solid ${LINE_SOFT}`,
            bgcolor: '#FFFFFF',
            objectFit: 'contain',
          }}
        />
      ) : (
        <Box sx={{ textAlign: 'center', color: INK_SOFT }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '12px',
              bgcolor: '#F1F5F9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 1.5,
            }}
          >
            <MedicationIcon sx={{ color: '#2563EB', fontSize: 30 }} />
          </Box>
          <Box sx={{ fontSize: 13, fontFamily: BODY_FONT, maxWidth: 190 }}>{placeholder}</Box>
        </Box>
      )}
    </Box>
  </Box>
);

interface IEditSellItemProps {
  isAdmin?: boolean;
}

export const EditSellItem:React.FunctionComponent<IEditSellItemProps> = ({ isAdmin }) => {
  const {id: productId} = useParams<any>();
  const { id: userId }: any = useContext(UserContext);
  const { token } = useAuth();
  const navigate  = useNavigate();
  const {addToast} = useContext(ToasterContext);
  const [drugData, setDrugData] = useState<any>(null);
  const [options, setOptions] = useState<any>([]);
  const [fdaLoading, setFdaLoading] = useState<boolean>(false);
  const [openDatePicker, setOpenDatePicker] = useState<boolean>(false);
  const [refrigerated, setRefrigerated] = useState<boolean>(false);
  const [type, setType] = useState<string>('FULL');
  const [sealed, setSealed] = useState<string>('');
  const [packageConditionType, setPackageConditionType] = useState<string>('');
  const [packageAvailable, setPackageAvailable] = useState<number>(1);
  const [price, setPrice] = useState<string>('');
  const [quantityPerPack, setQuantityPerPack] = useState<string>('');
  const [unitPrice, setUnitPrice] = useState<number>(0);
  const [fdaError, setFdaError] = useState<string>('');
  const [inputValue, setInputValue] = useState<string>('');
  const [searchInputValue, setSearchInputValue] = useState<string>('');
  const [imageUrl, setImageUrl] = useState<string>('');
  const [restrictedNdcs, setRestrictedNdcs] = useState<string[]>([]);
  const [isRestrictedUser, setIsRestrictedUser] = useState<boolean>(false);
  const [restrictionDataLoading, setRestrictionDataLoading] = useState<boolean>(true);
  const packageConditionRef = useRef<HTMLDivElement>(null);
  const medispanAbortControllerRef = useRef<AbortController | null>(null);

  const { register, handleSubmit, formState: { errors }, control, setValue } = useForm({
    resolver: yupResolver(postNewItemValidationSchema)
  });

  const {data: pharmacyUsersData, loading: pharmacyUsersLoading } = useQueryUniqueUsers({
    variables: { where: { id:  +userId } },
  });

  const {data: productData, loading: productDataLoading } = useQueryUniqueProduct({
    variables: { where: { id: Number(productId) } },
    fetchPolicy: 'no-cache'
  });
  const {data: packagesData, loading: packagesLoading} = useQueryPackages();
  const [updateProduct, { loading }] = useMutationUpdateProduct();

  // Fetch restriction data early
  useEffect(() => {
    const fetchRestrictionData = async () => {
      try {
        const userData = pharmacyUsersData?.findUniqueUser;
        const hasRestrictedRole = userData?.UserRoles?.some(
          (ur: any) => ur.Role?.key === 'restricted'
        );
        
        setIsRestrictedUser(hasRestrictedRole || false);
        
        if (hasRestrictedRole) {
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/users/restricted-ndcs?userId=${userId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`
              }
            }
          );
          setRestrictedNdcs((response.data.ndcs || []).map((ndc: string) => normalizeNdc(ndc)));
        } else {
          setRestrictedNdcs([]);
        }
      } catch (error) {
        console.error('Error fetching restriction data:', error);
        setRestrictedNdcs([]);
      } finally {
        setRestrictionDataLoading(false);
      }
    };

    if (pharmacyUsersLoading) {
      return;
    }

    if (!pharmacyUsersData?.findUniqueUser) {
      setRestrictionDataLoading(false);
      return;
    }

    fetchRestrictionData();
  }, [pharmacyUsersData, pharmacyUsersLoading, userId, token]);

  const normalizeNdcValue = (value: string) => value.replace(/-/g, '');

  const checkIfNdc = (value: string): boolean => /^\d{3}/.test(value);

  const capitalizeWords = (str: string) => {
    if (!str) return str;
    return str.split(' ').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };

  function generateDrugDescription(data: any) {
    if (!data) return '';
    const {
      generic_product_pack_code,
      package_description_code,
      package_quantity,
      package_size,
      package_size_uom,
      unit_dose_unit_use_pkg_code,
    } = data;
    const isUnitDose = unit_dose_unit_use_pkg_code === 'X' ? 'Yes' : 'No';
    return `Pack of ${package_quantity} units, each containing ${package_size} ${package_size_uom}, Unit Dose: ${isUnitDose}, Packaging Code: ${package_description_code}, Pack Code: ${generic_product_pack_code}`;
  }

  const handleMedispanSearch = async (value: string) => {
    if (medispanAbortControllerRef.current) {
      medispanAbortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    medispanAbortControllerRef.current = abortController;

    try {
      setFdaLoading(true);
      const api = `${process.env.REACT_APP_API_URL}/medispan/get?searchTerm=${encodeURIComponent(value)}`;
      const response = await axios.get(api, { signal: abortController.signal });
      setFdaLoading(false);

      if (!Array.isArray(response.data)) {
        setFdaError('Item with provided name and/or NDC was not found in FDA database. Please try a different name or continue manual entry below.');
        return;
      }

      const filteredData = response.data
        .filter((item: any) => item.product_ndc !== null)
        .filter((item: any) => {
          if (!checkIfNdc(value)) {
            return true;
          }
          const searchNdc = normalizeNdcValue(value);
          const productNdc = item.product_ndc ? normalizeNdcValue(item.product_ndc) : null;
          const packageNdc = item.package_ndc ? normalizeNdcValue(item.package_ndc) : null;
          return (productNdc && productNdc.startsWith(searchNdc)) ||
            (packageNdc && packageNdc.startsWith(searchNdc));
        })
        .map((item: any) => {
          // Capitalize the drug name
          if (item.brand_name) {
            item.brand_name = capitalizeWords(item.brand_name);
          }
          if (item.generic_name) {
            item.generic_name = capitalizeWords(item.generic_name);
          }
          if (item.substance_name) {
            item.substance_name = capitalizeWords(item.substance_name);
          }
          return item;
        });

      if (filteredData.length === 0) {
        setFdaError('Item with provided name and/or NDC was not found in FDA database. Please try a different name or continue manual entry below.');
      } else {
        setFdaError('');
        setOptions(filteredData);
      }
    } catch (error: any) {
      if (error?.code === 'ERR_CANCELED') {
        return;
      }
      setFdaLoading(false);
      addToast({
        type: NotificationType.Error,
        title: error.response ? error.message : "Server side error",
      });
    }
  };

  const debouncedMedispanSearch = useCallback(debounce((value: string) => {
    handleMedispanSearch(value);
  }, 500), []);

  useEffect(() => {
    return () => {
      if (medispanAbortControllerRef.current) {
        medispanAbortControllerRef.current.abort();
      }
    };
  }, [debouncedMedispanSearch]);

  const handleSearch = (value: string) => {
    const trimmedValue = value.trimStart();
    setOptions([]);
    setFdaError('');

    if (trimmedValue.length >= 3) {
      setFdaLoading(true);
      debouncedMedispanSearch(trimmedValue);
    } else {
      setFdaLoading(false);
    }
  }

  const handleAutofill = (value: any) => {
    if (value) {
      setValue('ndc', value.package_ndc || value.product_ndc, { shouldValidate: true });
      setValue('drugName', value.brand_name || value.generic_name, { shouldValidate: true });
      setValue('labelerName', value.labeler_name, { shouldValidate: true });
      setValue('form', value.dosage_form, { shouldValidate: true });
      if (value.active_ingredients) {
        setValue('strength', value.active_ingredients[0].strength, { shouldValidate: true });
      }
      setValue('notes', value.package_description || generateDrugDescription(value.gppc_data), { shouldValidate: true });
      setDrugData(value);
      if (value.storage_condition === "R" || value.storage_condition === "F") {
        setRefrigerated(true);
      } else {
        setRefrigerated(false);
      }

      const formatedNdc = value.product_ndc?.replace(/-/g, "");
      if (!formatedNdc) return;
      const imageUrlApi = `${process.env.REACT_APP_API_URL}/drug-images?ndc=${formatedNdc}`;
      axios.get(imageUrlApi).then((response) => {
        setImageUrl(response.data.imageUrl);
      });
    } else {
      setValue('ndc', '');
      setValue('drugName', '');
      setValue('labelerName', '');
      setValue('form', '');
      setValue('strength', '');
      setValue('notes', '');
      setDrugData(null);
      setImageUrl('');
    }
  }

  const handleChangePackageAvailable = (packageAvailable: number, isPlus: boolean = true) => {
    if (!isPlus && packageAvailable === 0) {
      return
    }

    if (isPlus) {
      setPackageAvailable(packageAvailable + 1)
    } else {
      setPackageAvailable(packageAvailable - 1);
    }
  }

  const handleChangeRefrigerated = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRefrigerated(event.target.checked);
  };

  const handleEditSellItemSubmit = (values: any) => {
    // Don't proceed if restriction data is still loading
    if (restrictionDataLoading) {
      addToast({ 
        type: NotificationType.Error, 
        title: 'Please wait, loading restriction data...' 
      });
      return;
    }

    // Check if trying to activate an expired product - expiration must be at least next month
    const nextMonth = dayjs().add(1, 'month').startOf('month');
    if (values.status === 'active' && dayjs(values.expDate).isBefore(nextMonth)) {
      addToast({ 
        type: NotificationType.Error, 
        title: 'Expiration date must be at least next month to activate this listing.' 
      });
      return;
    }

    // Check if trying to activate restricted product
    if (values.status === 'active' && isRestrictedUser && restrictedNdcs.includes(normalizeNdc(values.ndc))) {
      addToast({ 
        type: NotificationType.Error, 
        title: 'You can no longer make this item active. This NDC is restricted for you.' 
      });
      return;
    }

    // Manual validation for partial quantity
    if (type === 'PARTIAL' && (!values.partialQuantity || values.partialQuantity.trim() === '')) {
      addToast({ type: NotificationType.Error, title: 'Partial quantity is required when packaging type is Partial' });
      return;
    }

    // Manual validation for package condition
    if (!values.packageConditionType || values.packageConditionType.trim() === '') {
      addToast({ type: NotificationType.Error, title: 'Package condition is required' });
      // Scroll to package condition field
      setTimeout(() => {
        packageConditionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      return;
    }

    const selectedPackageIds: number[] = Array.isArray(values.optionalPackageConditions)
      ? values.optionalPackageConditions
      : [];
    const existingPackageIds: number[] = (productData?.findUniqueProduct?.ProductPackages || []).map(
      (pkg: any) => pkg.packageId
    );
    const data = selectedPackageIds
      .filter((packageId) => !existingPackageIds.includes(packageId))
      .map((packageId) => ({ packageId }));
    const deleted = existingPackageIds.filter((packageId) => !selectedPackageIds.includes(packageId));

    const requestData = {
      drugName: { set: values.drugName },
      ndc: { set: values.ndc },
      lotNumber: { set: values.lotNumber },
      expDate: { set: dayjs(values.expDate).date(15).hour(12).minute(0).second(0).millisecond(0).toISOString() },
      type: { set: type === 'FULL' ? 1 : 2 },
      packageCondition: { set: type === 'FULL' ? (sealed === 'SEALED' ? 1 : 2) : 2 }, // Default to 2 (Non-Sealed) for partial
      sealDetails: { set: values.packageConditionType || '' },
      labelerName: { set: values.labelerName },
      form: { set: values.form },
      maxPrice: { set: '0' },
      minPrice: { set: '0' },
      notes: { set: values.notes },
      packaging: { set: values.partialQuantity || '' },
      price: { set: values.price },
      priceOption: { set: 1 },
      quantity: { set: packageAvailable },
      quantityPerPack: { set: +values.quantityPerPack },
      storage: { set: 'Normal' },
      strength: { set: values.strength },
      unitPrice: { set: `${unitPrice}` },
      isFrozen: { set: refrigerated },
      wacDiscount: { set: 20 },
      ProductPackages: {
        createMany: {
          data: data,
          skipDuplicates: false
        },
        deleteMany: {
          packageId: {
            in: deleted
          }
        }
      },
      Pharmacy: isAdmin ? undefined : {
        connect: {
          id: pharmacyUsersData.findUniqueUser.PharmacyUsers[0].pharmacyId
        }
      },
      isActive: { set: values.status === 'active' },
    }
    updateProduct({ variables: { data: requestData, where: { id: Number(productId) } } }).then(() => {
      navigate(isAdmin ? routeConfigs.postings.path : routeConfigs.sell.path);
    }).catch((error: any) => {
      addToast({ type: NotificationType.Error, title: error.message  });
    });
  }

  useEffect(() => {
    if (productData) {
      setValue('ndc', productData.findUniqueProduct.ndc);
      setValue('lotNumber', productData.findUniqueProduct.lotNumber);
      setValue('drugName', productData.findUniqueProduct.drugName);
      setValue('labelerName', productData.findUniqueProduct.labelerName);
      setValue('form', productData.findUniqueProduct.form);
      setValue('strength', productData.findUniqueProduct.strength);
      setValue('notes', productData.findUniqueProduct.notes);
      setValue('price', Number(productData.findUniqueProduct.price).toFixed(2));
      setValue('quantityPerPack', productData.findUniqueProduct.quantityPerPack);
      setValue('expDate', dayjs(productData.findUniqueProduct.expDate));
      setValue('partialQuantity', productData.findUniqueProduct.packaging);
      setValue('packageConditionType', productData.findUniqueProduct.sealDetails || '');
      setValue(
        'optionalPackageConditions',
        (productData.findUniqueProduct.ProductPackages || []).map((pkg: any) => pkg.packageId)
      );
      setType(productData.findUniqueProduct.type === 1 ? 'FULL' : 'PARTIAL');
      setRefrigerated(productData.findUniqueProduct.isFrozen || false);
      setSealed(productData.findUniqueProduct.packageCondition === 1 ? 'SEALED' : 'NON_SEALED');
      setPackageAvailable(productData.findUniqueProduct.quantity);
      setPrice(Number(productData.findUniqueProduct.price).toFixed(2));
      setQuantityPerPack(String(productData.findUniqueProduct.quantityPerPack ?? ''));
      setPackageConditionType(productData.findUniqueProduct.sealDetails || '');
    }
  },[productData, setValue]);

  useEffect(() => {
    if (productData){
      const drugNdc = productData?.findUniqueProduct?.ndc;
      if (!drugNdc) return;
      const formattedNdc = drugNdc.replace(/-/g, "");
      const imageUrlApi = `${process.env.REACT_APP_API_URL}/drug-images?ndc=${formattedNdc}`;
      axios
        .get(imageUrlApi)
        .then((response) => {
          setImageUrl(response.data.imageUrl);
        })
        .catch((error) => {
          console.error("Error fetching drug image:", error);
        })
    }
  }, [productData])

  useEffect(() => {
    if (price !== '' && quantityPerPack !== '') {
      const unitPrice = Number(String(price).replace(/[^0-9.-]+/g, '')) / Number(quantityPerPack);

      setUnitPrice(!unitPrice ? 0 : unitPrice)
    }
  }, [price, quantityPerPack]);

  // Scroll to package condition field when validation error occurs
  useEffect(() => {
    if (errors.packageConditionType || errors.sealed) {
      setTimeout(() => {
        packageConditionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  }, [errors.packageConditionType, errors.sealed]);

  if (restrictionDataLoading || packagesLoading || productDataLoading || pharmacyUsersLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
        <BrandLoader size={48} />
      </Box>
    );
  }

  if (!productData?.findUniqueProduct) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 12, fontFamily: BODY_FONT, color: INK_SOFT }}>
        Product not found.
      </Box>
    );
  }

  return (
    <ThemeProvider theme={brandAuthTheme}>
    <Box
      component="form"
      onSubmit={handleSubmit(handleEditSellItemSubmit)}
      noValidate
      sx={{
        bgcolor: '#FFFFFF',
        border: `1px solid ${LINE}`,
        borderRadius: '14px',
        boxShadow: '0 1px 2px rgba(15,23,42,0.06), 0 8px 24px rgba(15,23,42,0.07)',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: BODY_FONT,
        '& .MuiFormLabel-root:not(.MuiInputLabel-root)': {
          fontSize: '12px',
          fontWeight: 600,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          color: INK_SOFT,
          fontFamily: BODY_FONT,
        },
      }}
    >
        <SectionHeader
          title="Product information"
          subtitle="Search the FDA database to autofill, or edit details manually."
        />
        <Grid container sx={{ px: 3, py: 2 }} spacing={3}>
          <Grid item xs={12} md={8}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
            <Autocomplete
              options={options}
              filterOptions={(option: any) => option}
              inputValue={searchInputValue}
              getOptionLabel={(option: any) => {
                if (typeof option === 'string') return option;
                if (!option) return '';
                return `${option?.brand_name || option?.generic_name} - ${option.product_ndc}`;
              }}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              value={drugData}
              freeSolo
              onChange={(event: any, newValue: string | null) => {
                if (newValue && typeof newValue === 'object') {
                  handleAutofill(newValue);
                }
              }}
              onInputChange={(event: any, newValue: string) => {
                const normalizedValue = newValue.trimStart();
                setSearchInputValue(normalizedValue);
                if (event?.type === 'change') {
                  setInputValue('');
                  handleSearch(normalizedValue);
                  const isNumber = /^\d+$/.test(normalizedValue);

                  if (isNumber && normalizedValue.length === 11) {
                    setInputValue(normalizedValue);
                  }
                }
              }}
              renderInput={(params) => (
                <>
                  <TextField
                    {...params}
                    label="Drug name or NDC number"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {fdaLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                  {fdaError !== '' && (
                    <Box sx={{ fontSize: '12px', mt: 1, color: '#C2410C' }}>
                      {fdaError}
                    </Box>
                  )}
                </>
              )}
            />
              </Grid>
              <Grid item xs={12}>
            <TextField
              disabled
              label="Drug name"
              id="drugName"
              fullWidth
              required
              defaultValue={productData.findUniqueProduct.drugName}
              helperText={errors.drugName?.message?.toString()}
              {...register('drugName')}
              error={!!errors.drugName}
            />
              </Grid>
              <Grid item xs={12} sm={6}>
            <TextField
              disabled
              label="NDC Number"
              id="ndc"
              fullWidth
              required
              defaultValue={productData.findUniqueProduct.ndc}
              helperText={errors.ndc?.message?.toString()}
              {...register('ndc')}
              error={!!errors.ndc}
            />
          </Grid>
              <Grid item xs={12} sm={6}>
            <TextField
              label="Lot Number"
              id="lotNumber"
              fullWidth
              required
              defaultValue={productData.findUniqueProduct.lotNumber}
              helperText={errors.lotNumber?.message?.toString()}
              {...register('lotNumber')}
              error={!!errors.lotNumber}
            />
          </Grid>
              <Grid item xs={12} sm={6}>
            <TextField
              disabled
              label="Manufacturer"
              id="labelerName"
              fullWidth
              required
              defaultValue={productData.findUniqueProduct.labelerName}
              helperText={errors.labelerName?.message?.toString()}
              {...register('labelerName')}
              error={!!errors.labelerName}
            />
          </Grid>
              <Grid item xs={12} sm={6}>
            <TextField
              disabled
              label="Dosage form"
              id="form"
              fullWidth
              required
              defaultValue={productData.findUniqueProduct.form}
              helperText={errors.form?.message?.toString()}
              {...register('form')}
              error={!!errors.form}
            />
          </Grid>
              <Grid item xs={12} sm={6}>
            <TextField
              disabled
              label="Strength"
              id="strength"
              fullWidth
              required
              defaultValue={productData.findUniqueProduct.strength}
              helperText={errors.strength?.message?.toString()}
              {...register('strength')}
              error={!!errors.strength}
            />
          </Grid>
              <Grid item xs={12} sm={6}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Controller
                control={control}
                name="expDate"
                defaultValue={dayjs(productData.findUniqueProduct.expDate)}
                render={({
                  field: { onChange, value },
                }) => (
                  <FormControl fullWidth>
                    <DatePicker
                      value={value ? dayjs(value) : null}
                      onChange={(date) => {
                        onChange(date && date.isValid() ? date : null);
                      }}
                      open={openDatePicker}
                      onClose={() => setOpenDatePicker(false)}
                      views={['month', 'year']}
                      minDate={dayjs().add(1, 'month').startOf('month')}
                      format={"MM/YYYY"}
                      label="Expiration date"
                      slotProps={{
                        textField: {
                          error: !!errors.expDate,
                          helperText: errors.expDate?.message?.toString(),
                          onClick: () => setOpenDatePicker(true)
                        },
                      }}
                    />
                  </FormControl>
                )}
              />
            </LocalizationProvider>
          </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={4}>
            <DrugImagePanel imageUrl={imageUrl} placeholder="No image available for this NDC." />
          </Grid>
        </Grid>
        <Divider sx={{ borderColor: LINE_SOFT }} />
        <SectionHeader
          title="Packaging & condition"
          subtitle="Tell buyers exactly what they'll receive."
        />
        <Grid container sx={{ px: 3, py: 2 }} spacing={2.5} alignItems="stretch">
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  disabled
                  checked={refrigerated}
                  onChange={(event) => {
                    handleChangeRefrigerated(event);
                  }}
                  inputProps={{ 'aria-label': 'controlled' }}
                />
              }
              label="Is product refrigerated"
            />
          </Grid>

          {/* Row: Packaging | Sealed */}
          <Grid item xs={12} md={6}>
            <FormLabel id='type-label'>Packaging</FormLabel>
            <Controller
              name='type'
              control={control}
              render={({ field }) => (
                <RadioGroup
                  {...field}
                  aria-labelledby='type-label'
                  row
                  id='type'
                  value={type}
                  onChange={(event) => {
                    setType(event.target.value)
                    if (event.target.value === 'PARTIAL') {
                      setSealed('NON_SEALED');
                      setPackageConditionType('');
                    } else {
                      setSealed('');
                      setPackageConditionType('');
                    }
                    field.onChange(event);
                  }}
                >
                  <FormControlLabel value="FULL" control={<Radio />} label="Full" />
                  <FormControlLabel value="PARTIAL" control={<Radio />} label="Partial" />
                  {type === 'PARTIAL' && (
                    <TextField
                      sx={{ mt: 1 }}
                      label="Partial quantity"
                      id="partialQuantity"
                      fullWidth
                      required
                      size="small"
                      defaultValue={productData.findUniqueProduct.packaging}
                      {...register('partialQuantity')}
                      helperText={errors.partialQuantity?.message?.toString()}
                      error={!!errors.partialQuantity}
                    />
                  )}
                </RadioGroup>
              )}
            />
          </Grid>
          <Grid item xs={12} md={6} ref={packageConditionRef}>
            <FormLabel id='sealed-label' error={!!errors.sealed}>Sealed</FormLabel>
            <Controller
              name='sealed'
              control={control}
              render={({ field }) => (
                <>
                  <RadioGroup
                    {...field}
                    aria-labelledby='sealed-label'
                    row
                    id='sealed'
                    value={sealed}
                    onChange={(event) => {
                      setSealed(event.target.value);
                      setPackageConditionType('');
                      setValue('packageConditionType', '');
                      field.onChange(event);
                    }}
                  >
                    <FormControlLabel value="SEALED" control={<Radio />} label="Sealed" disabled={type === 'PARTIAL'} />
                    <FormControlLabel value="NON_SEALED" control={<Radio />} label="Non-Sealed" disabled={type === 'PARTIAL'} />
                  </RadioGroup>
                  {errors.sealed && (
                    <Box sx={{ color: 'error.main', fontSize: 12, mt: 0.5 }}>
                      {typeof errors.sealed.message === 'string' ? errors.sealed.message : 'Please select Sealed or Non-Sealed'}
                    </Box>
                  )}
                </>
              )}
            />
          </Grid>

          {/* Row: Notes | (Package Condition + Additional details), heights matched */}
          <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
            <TextField
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                '& .MuiInputBase-root': {
                  flex: 1,
                  alignItems: 'stretch',
                },
                '& textarea': {
                  height: '100% !important',
                },
              }}
              label="Notes"
              id="notes"
              size="small"
              fullWidth
              multiline
              minRows={3}
              defaultValue={productData.findUniqueProduct.notes}
              helperText={errors.notes?.message?.toString()}
              {...register('notes')}
              error={!!errors.notes}
            />
          </Grid>
          <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Controller
              name='packageConditionType'
              control={control}
              defaultValue={packageConditionType}
              render={({ field }) => (
                <FormControl fullWidth required size="small">
                  <InputLabel id="package-condition-type-label">Package Condition</InputLabel>
                  <Select
                    {...field}
                    labelId="package-condition-type-label"
                    id="packageConditionType"
                    label="Package Condition"
                    value={field.value || ''}
                    onChange={(event) => {
                      const value = event.target.value;
                      setPackageConditionType(value);
                      field.onChange(value);
                    }}
                    disabled={(type === 'FULL' && !sealed) || (type === 'PARTIAL' && sealed !== 'NON_SEALED')}
                    error={!!errors.packageConditionType}
                  >
                    {sealed === 'SEALED' && [
                      <MenuItem value="SEALED_ORIGINAL_CONTAINER" key="SEALED_ORIGINAL_CONTAINER">Sealed Original Container</MenuItem>,
                      <MenuItem value="SEALED_ORIGINAL_BOTTLE_TORN_LABEL" key="SEALED_ORIGINAL_BOTTLE_TORN_LABEL">Sealed Original Bottle/Torn or label residue</MenuItem>
                    ]}
                    {sealed === 'NON_SEALED' && [
                      <MenuItem value="OPEN_ORIGINAL_CONTAINER" key="OPEN_ORIGINAL_CONTAINER">Open Original Container</MenuItem>,
                      <MenuItem value="OPEN_PRESCRIPTION_BOTTLE" key="OPEN_PRESCRIPTION_BOTTLE">Open in a Prescription bottle</MenuItem>,
                      <MenuItem value="OPEN_ORIGINAL_BOTTLE_TORN_LABEL" key="OPEN_ORIGINAL_BOTTLE_TORN_LABEL">Open Original bottle/Torn on label residue</MenuItem>
                    ]}
                    {!sealed && (
                      <MenuItem value="" disabled key="empty">Please select Sealed or Non-Sealed first</MenuItem>
                    )}
                  </Select>
                  {errors.packageConditionType && (
                    <Box sx={{ color: 'error.main', fontSize: 12, mt: 0.5 }}>
                      {typeof errors.packageConditionType.message === 'string' ? errors.packageConditionType.message : ''}
                    </Box>
                  )}
                </FormControl>
              )}
            />
            <Controller
              name="optionalPackageConditions"
              control={control}
              defaultValue={[]}
              render={({ field }) => {
                const selectedIds: number[] = field.value || [];
                return (
                  <FormControl fullWidth size="small">
                    <InputLabel id="optional-package-conditions-label">Additional details (optional)</InputLabel>
                    <Select
                      {...field}
                      labelId="optional-package-conditions-label"
                      id="optionalPackageConditions"
                      multiple
                      value={selectedIds}
                      label="Additional details (optional)"
                      onChange={(event) => {
                        const value = event.target.value;
                        field.onChange(typeof value === 'string' ? value.split(',').map(Number) : value);
                      }}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(selected as number[]).map((id) => {
                            const option = packagesData.findManyPackage.find((pkg: any) => pkg.id === id);
                            if (!option) return null;
                            const label = option.key === 'other' ? `${option.name} (Describe in Notes)` : option.name;
                            return (
                              <Chip
                                key={id}
                                size="small"
                                label={label}
                                sx={{ fontFamily: BODY_FONT, height: 24 }}
                              />
                            );
                          })}
                        </Box>
                      )}
                    >
                      {packagesData.findManyPackage.map((option: any) => {
                        const label = option.key === 'other' ? `${option.name} (Describe in Notes)` : option.name;
                        return (
                          <MenuItem key={option.id} value={option.id}>
                            <Checkbox checked={selectedIds.includes(option.id)} />
                            <ListItemText primary={label} />
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                );
              }}
            />
          </Grid>
        </Grid>
        <Divider sx={{ mt: 2, borderColor: LINE_SOFT }} />
        <SectionHeader
          title="Set price"
          subtitle="Unit price is calculated automatically from the pack price and size."
        />
        <Grid container sx={{ px: 3, py: 2 }} spacing={3} alignItems="flex-start">
          <Grid item xs={12} md={3}>
            <TextField
              label="Packages Available"
              id="packagesAvailable"
              required
              value={packageAvailable}
              InputProps={{
                readOnly: true,
                startAdornment: (
                  <IconButton
                    size="small"
                    color="primary"
                    aria-label="Decrease packages"
                    sx={{ p: '4px', mr: 0.5 }}
                    onClick={() => handleChangePackageAvailable(packageAvailable, false)}
                  >
                    <RemoveIcon fontSize="small" />
                  </IconButton>
                ),
                endAdornment: (
                  <IconButton
                    size="small"
                    color="primary"
                    aria-label="Increase packages"
                    sx={{ p: '4px', ml: 0.5 }}
                    onClick={() => handleChangePackageAvailable(packageAvailable)}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                ),
              }}
              inputProps={{
                style: { textAlign: 'center', fontWeight: 600, fontFamily: BODY_FONT },
              }}
              sx={{
                width: 160,
                minWidth: 160,
                maxWidth: 160,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Price"
              placeholder='$0.00'
              id="price"
              required
              fullWidth
              defaultValue={productData.findUniqueProduct.price}
              helperText={errors.price?.message?.toString()}
              {...register('price')}
              error={!!errors.price}
              onChange={(event) => setPrice(event.target.value)}
              onBlur={(event) => {
                setValue('price', event.target.value !== '' ? Number(event.target.value).toFixed(2) : '');
                setPrice(event.target.value !== '' ? Number(event.target.value).toFixed(2) : '');
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Original pack size"
              id="quantityPerPack"
              required
              fullWidth
              defaultValue={productData.findUniqueProduct.quantityPerPack}
              helperText={errors.quantityPerPack?.message?.toString()}
              {...register('quantityPerPack')}
              error={!!errors.quantityPerPack}
              onChange={(event) => setQuantityPerPack(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Unite price"
              placeholder='0.00 $'
              id="unitPrice"
              value={`$${unitPrice.toFixed(2)}`}
              required
              fullWidth
              disabled
            />
          </Grid>
        </Grid>
        <Divider sx={{ mt: 2, borderColor: LINE_SOFT }} />
        <SectionHeader
          title="Listing status"
          subtitle="Inactive listings are hidden from buyers."
        />
        <Grid container sx={{ px: 3, py: 2 }} spacing={3}>
          <Grid item xs={12} md={6}>
            <Controller
              name="status"
              control={control}
              defaultValue={productData.findUniqueProduct.isActive ? 'active' : 'inactive'}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel id="status-label">Status</InputLabel>
                  <Select
                    {...field}
                    labelId="status-label"
                    id="productStatus"
                    label="Status"
                    {...register('status')}
                    error={!!errors.status}
                    displayEmpty
                    inputProps={{ 'aria-label': 'Without label' }}
                  >
                    <MenuItem key='active' value={'active'}>Active</MenuItem>
                    <MenuItem key='inactive' value={'inactive'}>Inactive</MenuItem>
                  </Select>
                </FormControl>
              )}
            />
          </Grid>
        </Grid>
        <Box sx={{ display: 'flex', justifyContent: 'end', flexDirection: 'row', px: 3, py: 2, mt: 2, borderTop: `1px solid ${LINE_SOFT}`, bgcolor: '#F1F5F9' }}>
          <Button type="submit" size='large' sx={{ width: 200, borderRadius: '8px', fontWeight: 600 }} variant='contained' >
            Save
            { loading && <CircularProgress sx={{ml: 2}} size={14} color='inherit' />}
          </Button>
        </Box>
    </Box>
    </ThemeProvider>
  );
}

export default EditSellItem;